import { TriangleAlert } from "lucide-react";
import type { TimelineEvent } from "../contracts/event-timeline";
import { eventLabel, eventSurface } from "./blockStyles";
import { LiveDot } from "./LiveDot";
import { formatEventTooltip } from "./helpers";
import { useDisplayTimezone } from "@nexus/ui";

interface TimelineEventCardProps {
  event: TimelineEvent;
  leftPct: number;
  widthPct: number;
  top: number;
  height: number;
  ongoing: boolean;
  selected: boolean;
  interactive: boolean;
}

/**
 * An event on the track. Position encodes *when*, so the bar itself only needs
 * to say *what* and *is it healthy*: the name and, when relevant, the count of
 * unresolved anomalies via a discreet alert icon.
 */
export function TimelineEventCard({
  event,
  leftPct,
  widthPct,
  top,
  height,
  ongoing,
  selected,
  interactive,
}: TimelineEventCardProps) {
  const timezone = useDisplayTimezone();
  const problem = event.unresolved_anomaly_count > 0;

  return (
    <div
      data-kind="event"
      data-event-id={event.event_id}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      aria-label={event.event_label}
      className={[
        "group absolute flex items-center gap-2 overflow-hidden rounded-lg border px-2.5",
        "transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2",
        "focus-visible:ring-inset focus-visible:ring-focus",
        eventSurface({ selected }),
        interactive ? "cursor-pointer" : "cursor-default",
        selected ? "z-20" : "z-10",
      ].join(" ")}
      style={{ left: `${leftPct}%`, width: `${widthPct}%`, minWidth: 64, top, height }}
      title={formatEventTooltip(event, timezone)}
    >
      {ongoing && <LiveDot tone={problem ? "error" : "accent"} />}
      <span className={eventLabel(ongoing || selected)}>{event.event_label}</span>
      {problem && (
        <span className="flex shrink-0 items-center gap-1 text-status-error-text">
          <TriangleAlert className="h-3 w-3" aria-hidden />
          <span className="text-xs font-semibold tabular-nums">
            {event.unresolved_anomaly_count}
          </span>
        </span>
      )}
    </div>
  );
}
